import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { hasAnyContent } from '../store/migrate.ts';
import { useStore } from '../store/store.ts';
import { resetCursors, startSync } from '../sync/sync.ts';
import { setSyncStatus } from '../sync/status.ts';
import { LoginScreen } from './LoginScreen.tsx';
import { isSyncConfigured, supabase } from './supabase.ts';

type Gate = 'checking' | 'in' | 'out';

/**
 * Decides whether to show the app or the login screen.
 *
 * The rule that matters most here is what happens without a network. A
 * teacher mid-lesson on a tablet with no wifi must keep her journal, so:
 *
 *   - a stored session lets the app open immediately, with no network call
 *     on the critical path;
 *   - a failed token refresh never clears local data and never forces a
 *     logout, it just leaves sync reporting an error;
 *   - only an explicit sign-out returns to the login screen.
 *
 * With no backend configured the gate is transparent: the app behaves exactly
 * as it did before sync existed, local-only and always available.
 */
export function AuthGate({ children }: { children: ComponentChildren }) {
  const [gate, setGate] = useState<Gate>(isSyncConfigured ? 'checking' : 'in');

  useEffect(() => {
    if (!isSyncConfigured || !supabase) {
      setSyncStatus({ phase: 'disabled' });
      return;
    }

    let stopSync: (() => void) | undefined;

    // getSession reads the persisted session without a network round trip,
    // so this resolves offline just as well as online.
    void supabase.auth.getSession().then(({ data }) => {
      setGate(data.session ? 'in' : 'out');
      // Do not rely exclusively on INITIAL_SESSION: depending on when the
      // listener is registered, a restored session may already have emitted
      // it. The locally restored session must always start the sync engine.
      if (data.session) stopSync ??= startSync();
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setGate('out');
        resetCursors();
        stopSync?.();
        stopSync = undefined;
        return;
      }

      if (session) {
        setGate('in');
        stopSync ??= startSync();
      }
    });

    return () => {
      listener.subscription.unsubscribe();
      stopSync?.();
    };
  }, []);

  if (gate === 'checking') {
    return (
      <div class="loginScreen">
        <div class="card loginCard">
          <p class="muted">Ouverture du journal…</p>
        </div>
      </div>
    );
  }

  if (gate === 'out') return <LoginScreen />;

  return <>{children}</>;
}

/**
 * Push a pre-existing local journal to a freshly signed-in account.
 *
 * Rows migrated from the old single-file app arrive marked dirty, so the
 * ordinary push already carries them up; this only reports whether there was
 * anything to carry, for the message shown after first sign-in.
 */
export function hasLocalJournal(): boolean {
  return hasAnyContent(useStore.getState());
}
